import { Fragment, type ReactNode } from 'react';

import { cn } from '../../lib/utils';
import { FormField } from './FormField';
import { renderFormControl } from './formControl';
import type { FormConfig, FormFieldConfig, FormFieldDescription, FormSection } from './types';
import type { UseFormResult } from './useForm';

type Props<T, Ctx> = {
  form: UseFormResult<T>;
  config: FormConfig<T, Ctx>;
  context?: Ctx;
};

function resolveDescription<T, Ctx>(
  description: FormFieldDescription<T, Ctx> | undefined,
  form: UseFormResult<T>,
  ctx: Ctx,
): ReactNode {
  return typeof description === 'function' ? description(form, ctx) : description;
}

function Field<T, Ctx>({
  form,
  field,
  ctx,
}: {
  form: UseFormResult<T>;
  field: FormFieldConfig<T, Ctx>;
  ctx: Ctx;
}) {
  const fieldKey = field.key ?? field.id;
  const htmlFor = `field-${fieldKey}`;
  const span = field.colSpan === 1 ? undefined : 'col-span-2';
  const control = field.render
    ? field.render(form, ctx)
    : renderFormControl(form, field, ctx, htmlFor);
  const error = form.errors[field.id];

  if (field.inline) {
    return (
      <div className={cn('flex flex-col gap-1', span)}>
        {control}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <FormField
      className={span}
      label={field.label}
      htmlFor={htmlFor}
      error={error}
      description={resolveDescription(field.description, form, ctx)}
      required={field.required}
    >
      {control}
    </FormField>
  );
}

export function ConfiguredForm<T, Ctx = void>({ form, config, context }: Props<T, Ctx>) {
  const ctx = context as Ctx;
  // Flat `fields` collapse to one untitled section so both shapes share a path.
  const declared: FormSection<T, Ctx>[] = config.sections ?? [{ fields: config.fields ?? [] }];
  // Filtered before the map so the `i > 0` divider still counts rendered
  // sections — indexing the declared list would leave a leading rule.
  const sections = declared.filter(
    (section) => !section.visibleWhen || section.visibleWhen(form.values),
  );

  return (
    <div className="flex flex-col gap-5">
      {sections.map((section, i) => {
        const visibleFields = section.fields.filter(
          (field) => !field.visibleWhen || field.visibleWhen(form.values),
        );
        return (
          <Fragment key={i}>
            {i > 0 && <hr className="border-border" />}
            <div className="flex flex-col gap-4">
              {section.title != null && (
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-medium text-foreground">{section.title}</h3>
                  {section.titleAccessory}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                {visibleFields.map((field) => (
                  <Field key={field.key ?? field.id} form={form} field={field} ctx={ctx} />
                ))}
              </div>
              {section.description != null && (
                <p className="text-xs text-muted-foreground">
                  {resolveDescription(section.description, form, ctx)}
                </p>
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
